/**
 * EtfHoldings: compact dashboard widget showing each Bitcoin ETF position.
 *
 * For each holding:
 *   - Symbol + name
 *   - Current shares (sum of transactions)
 *   - Live price (with manual override fallback)
 *   - USD value
 *   - P/L since the first transaction with cost basis (if any),
 *     labelled "since start" because the user may have inherited
 *     cost-unknown shares.
 *
 * The layout is intentionally smaller than the BTC hero. This is
 * supplementary info, not the main act of the page.
 *
 * Patch I: section heading renamed from "Retirement plan holdings"
 * to "Bitcoin ETF positions". Many users hold these outside of
 * retirement accounts; the retirement framing was misleading.
 * Em-dashes scrubbed throughout.
 *
 * Patch EI: live-price writes go through setEtfLivePrice (no-save,
 * no-undo, DX semantics) instead of updateEtfHolding which fires
 * cloud saves via withUndo. Pre-EI this widget's 5-minute auto-refresh
 * (and the per-holding refresh button) triggered a cloud save on every
 * fetch even though the only change was a transient livePriceSnapshot.
 * DX patched Dashboard.tsx and App.tsx's boot-fetch but missed this
 * file - the diagnostic chain EA..EH pinned it down via
 * actionKeys: [etfHoldings, undoStack] on the cloud-save trigger log.
 * The manualPriceUsd path on the Purchases tab still uses
 * updateEtfHolding because that IS a real user edit that belongs in
 * the undo stack and should sync to the cloud.
 */

import { useAppStore } from '../state/store.js';
import { Decimal, ZERO } from '../engine/money.js';
import { formatUsd } from './Money.js';
import { fetchEtfPrice } from '../io/live/etf-price.js';
import { useAutoRefresh } from '../hooks/useAutoRefresh.js';
import type { EtfHolding, EtfTransaction } from '../engine/types.js';

/** Compute current shares and tracked cost basis for a holding. */
export function holdingPosition(
  holding: EtfHolding,
  txs: EtfTransaction[],
): {
  currentShares: Decimal;
  trackedCostUsd: Decimal;       // sum of (shares × pricePerShareUsd) for txs WITH price
  trackedShares: Decimal;        // shares with cost basis (excludes "starting balance")
  effectivePriceUsd: Decimal;    // manual override if set, else livePriceSnapshot, else 0
} {
  const myTxs = txs.filter(t => t.holdingId === holding.id);
  const currentShares = myTxs.reduce((acc, t) => acc.plus(t.shares), ZERO);
  let trackedCostUsd = ZERO;
  let trackedShares = ZERO;
  for (const t of myTxs) {
    if (t.pricePerShareUsd && t.shares.greaterThan(0)) {
      trackedCostUsd = trackedCostUsd.plus(t.shares.times(t.pricePerShareUsd));
      trackedShares = trackedShares.plus(t.shares);
    }
  }
  const effectivePriceUsd =
    holding.manualPriceUsd ?? holding.livePriceSnapshot?.priceUsd ?? ZERO;
  return { currentShares, trackedCostUsd, trackedShares, effectivePriceUsd };
}

export function EtfHoldings(): JSX.Element {
  const holdings = useAppStore(s => s.etfHoldings);
  const txs = useAppStore(s => s.etfTransactions);
  // Patch EI: swap updateEtfHolding (save-firing, withUndo-wrapped) for
  // setEtfLivePrice (no-save, no-undo) on the live-price write path.
  // Both refreshPrice (single-holding button) and refreshAll (5min auto)
  // were the path the diagnostic chain pinned as the source of the
  // spurious 2-3 minute cloud saves on PWA wake.
  const setEtfLivePrice = useAppStore(s => s.setEtfLivePrice);
  const etfPriceStatus = useAppStore(s => s.etfPriceStatus);
  const etfPriceError = useAppStore(s => s.etfPriceError);
  const setEtfPriceStatus = useAppStore(s => s.setEtfPriceStatus);

  const refreshPrice = async (holding: EtfHolding): Promise<void> => {
    setEtfPriceStatus('loading');
    try {
      const snap = await fetchEtfPrice(holding.symbol);
      setEtfLivePrice(holding.id, snap);
      setEtfPriceStatus('ok');
    } catch (e) {
      setEtfPriceStatus('error', e instanceof Error ? e.message : String(e));
    }
  };

  const refreshAll = async (): Promise<void> => {
    setEtfPriceStatus('loading');
    let lastErr: string | null = null;
    for (const h of holdings) {
      try {
        const snap = await fetchEtfPrice(h.symbol);
        setEtfLivePrice(h.id, snap);
      } catch (e) {
        lastErr = e instanceof Error ? e.message : String(e);
      }
    }
    setEtfPriceStatus(lastErr ? 'error' : 'ok', lastErr);
  };

  // Auto-refresh: on mount, on tab return, and every 5 minutes while the tab is visible.
  // The 30s minGap prevents thrashing when the user rapidly switches tabs.
  useAutoRefresh(refreshAll, {
    enabled: holdings.length > 0,
    intervalMs: 5 * 60 * 1000,
    minGapMs: 30_000,
  });

  if (holdings.length === 0) {
    return (
      <section className="section">
        <p className="section-eyebrow">Bitcoin ETF positions</p>
        <p className="muted" style={{ fontSize: 'var(--fs-small)' }}>
          No ETF holdings tracked yet. Add some on the Purchases tab.
        </p>
      </section>
    );
  }

  return (
    <section className="section">
      <p className="section-eyebrow">Bitcoin ETF positions</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--gap-l)' }}>
        {holdings.map(h => {
          const pos = holdingPosition(h, txs);
          const value = pos.currentShares.times(pos.effectivePriceUsd);
          const havePrice = !pos.effectivePriceUsd.isZero();
          const haveBasis = pos.trackedShares.greaterThan(0);
          const trackedValue = haveBasis ? pos.trackedShares.times(pos.effectivePriceUsd) : ZERO;
          const trackedPL = haveBasis ? trackedValue.minus(pos.trackedCostUsd) : ZERO;
          const trackedPLPct = haveBasis && !pos.trackedCostUsd.isZero()
            ? trackedPL.div(pos.trackedCostUsd).times(100)
            : ZERO;
          const isManual = !!h.manualPriceUsd;

          return (
            <div
              key={h.id}
              style={{
                flex: '1 1 240px',
                minWidth: 240,
                padding: 'var(--gap-s) 0',
                borderTop: '1px solid var(--rule)',
              }}
            >
              <div className="row between" style={{ alignItems: 'baseline' }}>
                <span className="mono" style={{ fontWeight: 600, fontSize: 'var(--fs-base)' }}>{h.symbol}</span>
                <span className="mono faint" style={{ fontSize: 'var(--fs-micro)' }}>
                  {pos.currentShares.toFixed(0)} shares
                </span>
              </div>
              <div className="muted" style={{ fontSize: 'var(--fs-micro)', marginBottom: 4 }}>{h.name}</div>
              <div className="row between" style={{ alignItems: 'baseline', marginTop: 6 }}>
                <span className="mono" style={{ fontSize: 'var(--fs-lead)' }}>
                  {havePrice ? <>${formatUsd(value, { decimals: 0 })}</> : '-'}
                </span>
                <span className="mono faint" style={{ fontSize: 'var(--fs-micro)' }}>
                  {havePrice ? <>@ ${formatUsd(pos.effectivePriceUsd, { decimals: 2 })}</> : ''}
                  {isManual ? ' (manual)' : ''}
                </span>
              </div>
              {haveBasis ? (
                <div className="mono" style={{ fontSize: 'var(--fs-micro)', marginTop: 4 }}>
                  <span className="faint">since start: </span>
                  <span className={trackedPL.greaterThanOrEqualTo(0) ? 'delta pos' : 'delta neg'}>
                    {trackedPL.greaterThanOrEqualTo(0) ? '+' : ''}${formatUsd(trackedPL, { decimals: 0 })}
                    {' '}({trackedPLPct.greaterThanOrEqualTo(0) ? '+' : ''}{trackedPLPct.toFixed(1)}%)
                  </span>
                </div>
              ) : (
                <div className="faint mono" style={{ fontSize: 'var(--fs-micro)', marginTop: 4 }}>
                  no cost basis · P/L tracked from new purchases
                </div>
              )}
              <button
                onClick={() => void refreshPrice(h)}
                disabled={etfPriceStatus === 'loading'}
                style={{ fontSize: 'var(--fs-micro)', padding: '2px 8px', marginTop: 6 }}
                className="ghost"
                title={h.livePriceSnapshot
                  ? `last fetched ${new Date(h.livePriceSnapshot.fetchedAt).toLocaleTimeString()}`
                  : 'fetch price'}
              >
                refresh
              </button>
            </div>
          );
        })}
      </div>
      {etfPriceError ? (
        <div className="notice" style={{ marginTop: 'var(--gap-m)', borderLeftColor: 'var(--neg)', fontSize: 'var(--fs-small)' }}>
          <strong>ETF price fetch failed.</strong> {etfPriceError}
          {' '}You can set a manual price per holding via the Purchases tab.
        </div>
      ) : null}
    </section>
  );
}
